import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import { Code, ShieldCheck, FileJson, Activity, Building2, ArrowRight, Copy, Check } from "lucide-react";

const ENDPOINTS = [
  {
    id: "compliance-scan",
    name: "Compliance Score",
    method: "POST",
    path: "/api/v1/compliance/scan",
    desc: "Score any text — financial promotion, email, social post, web copy — against FCA, ASA and ICO rulesets in real time.",
    icon: ShieldCheck,
    request: `{
  "content": "Risk-free investment with guaranteed 20% returns",
  "document_type": "financial_promotion",
  "ruleset": "all"
}`,
    response: `{
  "score": 15,
  "verdict": "non_compliant",
  "flags": [{
    "rule_reference": "FCA Financial Promotions — Clear, fair and not misleading",
    "severity": "critical",
    "excerpt": "Risk-free investment with guaranteed 20% returns",
    "explanation": "...",
    "suggested_fix": "..."
  }],
  "summary": "...",
  "rules_checked": ["FCA COBS 4.2", "ASA CAP Code", "ICO PECR"],
  "processing_ms": 1240
}`,
  },
  {
    id: "client-pack",
    name: "Client Onboarding Pack",
    method: "POST",
    path: "/api/v1/clients/{id}/pack",
    desc: "Programmatically generate a complete onboarding pack — JSON or branded PDF — for any client. Perfect for back-office CRM integrations.",
    icon: FileJson,
    request: `{
  "format": "json"
}`,
    response: `{
  "client": { "id": "...", "first_name": "...", "status": "complete" },
  "identity_verification": { "status": "verified" },
  "aml_screening": { "status": "clear" },
  "questionnaire_answers": { ... },
  "documents": [ ... ],
  "esignature": { "ip_address": "...", "signed_at": "..." },
  "compliance_scans": [ ... ],
  "audit_trail": [ ... ],
  "chain_integrity": { "valid": true, "checked": 47 }
}`,
  },
  {
    id: "audit-verify",
    name: "Audit Trail Verification",
    method: "GET",
    path: "/api/v1/audit/verify",
    desc: "External auditors and the FCA can verify the cryptographic integrity of your audit trail without needing a Compliche login.",
    icon: Activity,
    request: `# Optional query params:
?date_from=2026-01-01&date_to=2026-02-17&client_id=...`,
    response: `{
  "chain_intact": true,
  "total_events": 1247,
  "first_event_hash": "7d22b6cd...",
  "last_event_hash": "84549b66...",
  "broken_links": [],
  "verification_timestamp": "2026-02-17T10:23:00Z",
  "verifier": "Compliche Platform v1.0"
}`,
  },
  {
    id: "firms-verify",
    name: "FCA Firm Verification",
    method: "GET",
    path: "/api/v1/firms/verify",
    desc: "Verify any UK firm against Companies House and the FCA register before doing business with them.",
    icon: Building2,
    request: `?firm_name=Acme%20Capital%20Ltd
# OR
?company_number=12345678
# OR
?fca_reference=987654`,
    response: `{
  "companies_house": {
    "status": "active",
    "company_name": "Acme Capital Ltd",
    "company_number": "12345678",
    "incorporation_date": "2014-03-12",
    "registered_address": { ... },
    "directors": [ "Jane Doe", "John Smith" ]
  },
  "fca_register": {
    "status": "authorised",
    "fca_reference_number": "987654",
    "permissions": [ ... ]
  }
}`,
  },
  {
    id: "regulatory-check",
    name: "Regulatory Check",
    method: "POST",
    path: "/api/v1/regulatory/check",
    desc: "Assess any document — client agreement, terms of business, internal policy — against the full regulatory framework applicable to your firm type.",
    icon: Code,
    request: `{
  "text": "Full document text...",
  "firm_type": "ifa",
  "jurisdiction": "UK"
}`,
    response: `{
  "applicable_regulations": [{
    "regulation": "FCA COBS",
    "status": "partially_met",
    "evidence": "...",
    "recommended_action": "..."
  }],
  "overall_readiness_score": 72,
  "firm_type": "ifa",
  "ruleset_version": "compliche-2026.02"
}`,
  },
];

function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="absolute top-2 right-2 btn btn-ghost btn-sm text-white/70 hover:text-white">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
      <pre className="bg-navy-900 text-white t-mono text-xs leading-relaxed p-4 pr-12 rounded-lg overflow-x-auto"><code>{code}</code></pre>
      {lang && <span className="absolute bottom-2 right-3 t-caption text-white/30">{lang}</span>}
    </div>
  );
}

export default function Developers() {
  const [endpoint, setEndpoint] = useState(ENDPOINTS[0]);
  const curl = `curl -X ${endpoint.method} '${endpoint.path.replace("{id}", "client_id")}' \\
  -H 'X-API-Key: ck_live_***' \\
  -H 'Content-Type: application/json'${endpoint.method === "POST" ? " \\\n  -d '" + endpoint.request.replace(/\n/g, " ").replace(/  +/g, " ") + "'" : ""}`;
  const python = `import os, requests
r = requests.${endpoint.method.toLowerCase()}(
    "https://api.compliche.com${endpoint.path.replace("{id}", "client_id")}",
    headers={"X-API-Key": os.environ["COMPLICHE_KEY"]},
${endpoint.method === "POST" ? `    json=${endpoint.request},\n` : ""})
print(r.json())`;
  const js = `const res = await fetch("https://api.compliche.com${endpoint.path.replace("{id}", "client_id")}", {
  method: "${endpoint.method}",
  headers: { "X-API-Key": process.env.COMPLICHE_KEY, "Content-Type": "application/json" },${endpoint.method === "POST" ? `\n  body: JSON.stringify(${endpoint.request}),` : ""}
});
const data = await res.json();`;

  return (
    <div className="min-h-screen bg-navy-900 text-white">
      <MarketingNav />
      <section className="pt-32 pb-12 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <span className="badge2" style={{ background: "rgba(79,70,229,0.16)", color: "#A5B4FC", borderColor: "rgba(79,70,229,0.4)" }}>API · v1</span>
            <h1 className="t-display mt-5 leading-tight" style={{ fontSize: "clamp(36px, 5vw, 56px)" }}>Compliance, programmable.</h1>
            <p className="mt-5 t-body-lg text-white/70">Five public REST endpoints that put real-time FCA-aware compliance scoring, cryptographic audit verification, firm verification and regulatory readiness into your stack.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/app/settings#api" className="btn btn-primary btn-lg">Get an API key <ArrowRight size={16} /></Link>
              <a href="#endpoints" className="btn btn-secondary-on-dark btn-lg">See endpoints</a>
            </div>
            <p className="mt-4 t-caption text-white/50">Authentication: <code className="t-mono">X-API-Key: ck_live_…</code> · 1,000 calls/mo on Professional · Unlimited on Enterprise</p>
          </div>
        </div>
      </section>

      <section id="endpoints" className="bg-white text-ink py-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[280px_1fr] gap-8">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="t-label text-slate2 mb-3">Endpoints</div>
            <nav className="space-y-1">
              {ENDPOINTS.map((e) => (
                <button key={e.id} onClick={() => setEndpoint(e)} className={`w-full text-left px-3 py-2.5 rounded-md t-body-sm flex items-center gap-2 ${endpoint.id === e.id ? "bg-indigo2 text-white" : "text-slate2 hover:bg-surface hover:text-ink"}`} data-testid={`dev-nav-${e.id}`}>
                  <e.icon size={16} className="flex-shrink-0" />
                  <span className="truncate">{e.name}</span>
                </button>
              ))}
            </nav>
            <div className="mt-8 p-4 bg-surface rounded-md border border-border2">
              <div className="t-h4 mb-1">Need help?</div>
              <p className="t-caption mb-3">We're a small team — real engineers reply.</p>
              <a href="mailto:developers@compliche.com" className="t-body-sm text-indigo2 hover:underline">developers@compliche.com →</a>
            </div>
          </aside>

          <article className="space-y-6 min-w-0">
            <header>
              <span className={`badge2 ${endpoint.method === "GET" ? "badge-verified" : "badge-locked"} t-mono`}>{endpoint.method}</span>
              <code className="ml-2 t-mono text-base">{endpoint.path}</code>
              <h2 className="t-h2 mt-3">{endpoint.name}</h2>
              <p className="t-body text-slate2 mt-1">{endpoint.desc}</p>
            </header>

            <div>
              <h3 className="t-h4 mb-2">Request</h3>
              <CodeBlock code={endpoint.request} lang="JSON" />
            </div>
            <div>
              <h3 className="t-h4 mb-2">Response</h3>
              <CodeBlock code={endpoint.response} lang="JSON" />
            </div>

            <div>
              <h3 className="t-h4 mb-2">Code examples</h3>
              <div className="space-y-4">
                <div><div className="t-caption mb-1">cURL</div><CodeBlock code={curl} lang="bash" /></div>
                <div><div className="t-caption mb-1">Python</div><CodeBlock code={python} lang="python" /></div>
                <div><div className="t-caption mb-1">JavaScript</div><CodeBlock code={js} lang="javascript" /></div>
              </div>
            </div>

            <div className="card2 p-5 border-l-4 border-indigo2">
              <h3 className="t-h4 mb-2">Errors</h3>
              <table className="w-full">
                <thead><tr className="text-left text-slate2"><th className="t-label py-2">Code</th><th className="t-label py-2">Meaning</th></tr></thead>
                <tbody className="t-body-sm">
                  <tr className="border-t border-border2"><td className="py-2 t-mono">401</td><td className="py-2">Missing or invalid X-API-Key</td></tr>
                  <tr className="border-t border-border2"><td className="py-2 t-mono">403</td><td className="py-2">Plan does not include API access</td></tr>
                  <tr className="border-t border-border2"><td className="py-2 t-mono">404</td><td className="py-2">Resource not found</td></tr>
                  <tr className="border-t border-border2"><td className="py-2 t-mono">429</td><td className="py-2">Monthly rate limit reached</td></tr>
                  <tr className="border-t border-border2"><td className="py-2 t-mono">5xx</td><td className="py-2">Server error — automatically reported to our team</td></tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
